import React from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { InfoCard, StylovyzeTooltip } from '@innovyze/stylovyze';
import HelpIcon from '@mui/icons-material/Help';
import { Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';
import { Content, Heading, Key } from './RiskNormalize.styles';

interface RiskNormalizeProps {
	isLoading: boolean;
	normalizeSettings: { useNormalize: boolean; normalizeValue: number };
	onNormalizeChanged(useNormalize: boolean, normalizeValue: number): void;
}

export const RiskNormalize = ({
	isLoading,
	normalizeSettings,
	onNormalizeChanged,
}: RiskNormalizeProps) => {
	const { t } = useGlobalization();

	const contents = () => {
		return (
			<>
				<Heading>
					{t('What does it mean to Normalize Risk Scores?')}
				</Heading>
				<Content>
					<b>{t('Normalization Equation:')}</b>
					<br />
					{t('( PRS / HCRS ) * NV')}
					<br />
					<br />
					<Key>
						{t('    PRS = Pipe Risk Score')}
						<br />
						{t('    HCRS  = Highest Calculated Risk Score')}
						<br />
						{t('    NV = Normalization Value')}
					</Key>
					<br />
					<b>{t('Example:')}</b>
					<br />
					{t('Pipe Risk Score = 233')}
					<br />
					{t('Highest Calculated Risk Score = 545')}
					<br />
					{t('Normalization Value = 100')}
					<br />
					<br />
					{t('Normalized Score = (233/545)*100 = 42.75')}
					<br />
				</Content>
			</>
		);
	};

	const onCheckUseNormalization = () => {
		onNormalizeChanged(
			!normalizeSettings.useNormalize,
			normalizeSettings.normalizeValue,
		);
	};

	const onNormalizeValueChange = (value: number) => {
		onNormalizeChanged(normalizeSettings.useNormalize, value);
	};

	const cardTitle = () => {
		return (
			<Stack direction={'row'} alignItems={'center'}>
				{t('Normalize Risk Scores')}
				<StylovyzeTooltip title={contents()} padded>
					<HelpIcon
						color="secondary"
						style={{ marginLeft: '10px' }}
					/>
				</StylovyzeTooltip>
			</Stack>
		);
	};

	return (
		<InfoCard title={cardTitle()}>
			<FormControlLabel
				disabled={isLoading}
				control={
					<Checkbox
						checked={normalizeSettings.useNormalize}
						onChange={onCheckUseNormalization}
					/>
				}
				label={t('Use Normalization 0 to')}
			/>
			<TextField
				disabled={isLoading || !normalizeSettings.useNormalize}
				style={{
					width: '100px',
					verticalAlign: 'text-top',
				}}
				value={normalizeSettings.normalizeValue}
				onChange={e => {
					let value = parseFloat(e.target.value);
					if (!isNaN(value)) {
						if (value < 1) value = 1;

						value = Math.round(value * 100) / 100;
					}

					onNormalizeValueChange(value);
				}}
				onBlur={e => {
					let value = parseFloat(e.target.value);
					if (isNaN(value)) value = 1;
					onNormalizeValueChange(Math.round(value * 100) / 100);
				}}
				inputProps={{
					step: '1',
					min: 1,
				}}
				variant="standard"
				type="number"
				size="small"
			/>
		</InfoCard>
	);
};
